import React, { useCallback } from 'react';

import { Maybe } from 'types/graphql-api.generated';

import withMQState, { WithMQStateProps } from 'common/components/withMQState';
import { TECHNOS_PREMIUM } from 'common/configuration/constants';
import { MQ_SMALL } from 'common/constants/MediaQueries';
import {
  BOOKING,
  LOYALTY_CARDS,
  SHOWTIMES_COMFORT,
  SHOWTIMES_EXPERIENCE,
  SHOWTIMES_EXPERIENCES_FILTER_TAGS,
  SHOWTIMES_PICTURE,
  SHOWTIMES_PROJECTION,
  SHOWTIMES_SOUND,
  SHOWTIMES_VERSION
} from 'common/constants/Showtimes';
import { ShowtimeFilterButtonTracking } from 'common/constants/trackingEventsNames';
import trans from 'common/tools/translations/trans';

import FilterButton from 'website/components/showtimes/FilterButton';
import FilterDropdownSeeMore from 'website/components/showtimes/FilterDropdownSeeMore';
import FilterDropdownSimple from 'website/components/showtimes/FilterDropdownSimple';
import FilterDropdownsDesktop from 'website/components/showtimes/FilterDropdownsDesktop';
import {
  VERSION_GROUP_ORDER,
  versionGroupKeyOf
} from 'website/containers/showtimes/utils';
import { Filter, FilterValue } from 'website/containers/showtimes/utils/types';

export type OnFilterChange = (
  entityGroup: string,
  entityKey?: string | null
) => void;

export type PageFiltersProps = {
  filters?: Maybe<Filter[]>;
  onFilterChange: OnFilterChange;
  seeMore?: boolean;
  versionDisabled: boolean;
  tracking: ShowtimeFilterButtonTracking;
};

export type FilterItem = FilterValue & {
  originalKey: string;
  unavailable?: boolean;
};

export type FilterRewritten = { label: string; values: FilterItem[] };

export type FiltersRewritten = Record<string, FilterRewritten>;

const FILTER_NAMES = [
  BOOKING,
  LOYALTY_CARDS,
  SHOWTIMES_PROJECTION,
  SHOWTIMES_VERSION,
  SHOWTIMES_PICTURE,
  SHOWTIMES_SOUND,
  SHOWTIMES_EXPERIENCE,
  SHOWTIMES_COMFORT
];

const EXPERIENCE_FILTER_NAMES = [
  SHOWTIMES_COMFORT,
  SHOWTIMES_EXPERIENCE,
  SHOWTIMES_PICTURE,
  SHOWTIMES_PROJECTION,
  SHOWTIMES_SOUND
];

const isVersionFilter = (filterName: string) =>
  filterName === SHOWTIMES_VERSION;

const versionGroupOf = (item: Maybe<FilterValue>) =>
  versionGroupKeyOf(item?.key);

/**
 * return translated label for filter version
 */
const getTranslatedFilterLabel = (
  filterName: string,
  item: Maybe<FilterValue>
) => {
  if (!item) return undefined;

  if (!isVersionFilter(filterName)) {
    return item.translation ? item.translation.name : item.key;
  }

  const groupKey = versionGroupOf(item);

  const key = groupKey ?? item.key?.toLowerCase();

  return key ? trans(`showtimes.version.${key}`) : undefined;
};

const initializeFilterGroupIfNotExists = (
  filtersRewritten: FiltersRewritten,
  mergedName: string
) => {
  if (!filtersRewritten[mergedName]) {
    filtersRewritten[mergedName] = {
      label:
        mergedName === 'theater_has_booking'
          ? 'theater_has_booking'
          : trans(
              mergedName === SHOWTIMES_VERSION
                ? 'showtimes.version'
                : mergedName
            ),
      values: []
    };
  }
};

const shouldAddFilterValue = (item: Maybe<FilterValue>, filterName: string) =>
  !!item?.key &&
  (!EXPERIENCE_FILTER_NAMES.includes(filterName) ||
    SHOWTIMES_EXPERIENCES_FILTER_TAGS.has(item.translation?.tag ?? ''));

const addFilterValue = (
  filtersRewritten: FiltersRewritten,
  mergedName: string,
  filterName: string,
  item: Maybe<FilterValue>
) => {
  filtersRewritten[mergedName].values.push({
    originalKey: filterName,
    label: getTranslatedFilterLabel(filterName, item) ?? undefined,
    ...item
  });
};

// Version filter values are displayed one per group (VO/VOST/VF/VF ST-SME),
// in VERSION_GROUP_ORDER, regardless of the ES doc_count order they arrive in.
const addVersionFilterValues = (
  filtersRewritten: FiltersRewritten,
  mergedName: string,
  filterName: string,
  values: FilterValue[]
) => {
  VERSION_GROUP_ORDER.forEach(groupKey => {
    const groupValues = values.filter(
      value => versionGroupOf(value) === groupKey
    );
    if (groupValues.length === 0) {
      return;
    }

    // reflect the whole group's state, not just the arbitrary first member's
    const item = {
      ...groupValues[0],
      active: groupValues.some(value => value.active),
      unavailable: groupValues.every(value => value.unavailable)
    };

    addFilterValue(filtersRewritten, mergedName, filterName, item);
  });
};

// Sorting function for premium technologies
const sortPremiumTechnologiesFirst = (item: FilterItem) => {
  if (
    TECHNOS_PREMIUM &&
    item?.translation?.tag &&
    TECHNOS_PREMIUM[item.translation.tag] &&
    TECHNOS_PREMIUM[item.translation.tag].premium
  ) {
    return -1;
  }
  return 0;
};

// Function to sort filters
const sortFilters = (filtersRewritten: FiltersRewritten) => {
  const sortedFilters: FiltersRewritten = {};

  // Use Object.entries to iterate over the object
  for (const [filterGroupKey, filterGroupValue] of Object.entries(
    filtersRewritten
  )) {
    const { values } = filterGroupValue;

    sortedFilters[filterGroupKey] = {
      ...filterGroupValue,
      values: values.sort(sortPremiumTechnologiesFirst)
    };
  }

  return sortedFilters;
};

const buildFilterDataModel = (
  filters: Maybe<Filter[]> | undefined,
  filterName: string,
  mergedName: string,
  filtersRewritten: FiltersRewritten
) => {
  const filterGroup = filters?.find(filter => filter.name === filterName);
  const values = filterGroup?.values?.filter(
    (item): item is FilterValue => !!item
  );

  if (!filterGroup || !values) {
    return;
  }

  // don't display any filter if only one version/projection is available
  // example: if all movies are in VF, the filter VF is useless
  // for the version filter, "available options" means distinct groups
  // (VO/VOST/VF/VF ST-SME), not distinct raw ES keys
  const availableCount = isVersionFilter(filterName)
    ? new Set(values.map(versionGroupOf).filter(Boolean)).size
    : values.length;

  if (
    [SHOWTIMES_PROJECTION, SHOWTIMES_VERSION].includes(filterName) &&
    availableCount <= 1
  ) {
    return;
  }

  initializeFilterGroupIfNotExists(filtersRewritten, mergedName);

  if (isVersionFilter(filterName)) {
    addVersionFilterValues(filtersRewritten, mergedName, filterName, values);
  } else {
    values
      .filter(item => shouldAddFilterValue(item, filterName))
      .forEach(item =>
        addFilterValue(filtersRewritten, mergedName, filterName, item)
      );
  }
};

const PageFilters = ({
  filters,
  mqState,
  onFilterChange,
  seeMore,
  versionDisabled,
  tracking
}: PageFiltersProps & WithMQStateProps) => {
  const buildBookingButtonComponent = useCallback(
    (filtersRewritten: FiltersRewritten) => {
      if (!filtersRewritten[BOOKING]) {
        return null;
      }

      const filterValues = filtersRewritten[BOOKING].values;

      return filterValues.length ? (
        <FilterButton
          active={filterValues[0].active}
          disabled={filterValues[0].unavailable}
          label={filterValues[0].label ?? ''}
          extraClasses="showtime-filter-display-button"
          onClick={onFilterChange.bind(null, BOOKING, BOOKING)}
          tracking={tracking}
        />
      ) : null;
    },
    [onFilterChange, tracking]
  );

  const rewriteFilters = useCallback(() => {
    const filtersRewritten: FiltersRewritten = {};

    FILTER_NAMES.forEach(filterName => {
      if (EXPERIENCE_FILTER_NAMES.includes(filterName)) {
        // Merge Experience filters
        buildFilterDataModel(
          filters,
          filterName,
          SHOWTIMES_EXPERIENCE,
          filtersRewritten
        );
      } else {
        buildFilterDataModel(filters, filterName, filterName, filtersRewritten);
      }
    });

    [SHOWTIMES_EXPERIENCE, LOYALTY_CARDS].forEach(filterName => {
      if (filtersRewritten[filterName]?.values.length === 0) {
        delete filtersRewritten[filterName];
      }
    });

    return filtersRewritten;
  }, [filters]);

  const filtersRewritten = rewriteFilters();

  // display booking button only if available
  const bookingButtonComponent = buildBookingButtonComponent(filtersRewritten);

  return (
    <div className="showtimes-filters showtimes-filters-bar">
      <FilterDropdownSimple
        disabled={versionDisabled}
        filterModel={filtersRewritten[SHOWTIMES_VERSION]}
        onFilterChange={onFilterChange}
        tracking={tracking}
      />
      <FilterDropdownsDesktop
        filters={sortFilters(filtersRewritten)}
        mqState={mqState}
        onFilterChange={onFilterChange}
        tracking={tracking}
        responsive={!!seeMore}
      />
      {mqState > MQ_SMALL && (
        <FilterDropdownSimple
          filterModel={filtersRewritten[LOYALTY_CARDS]}
          onFilterChange={onFilterChange}
          tracking={tracking}
        />
      )}
      {mqState > MQ_SMALL && bookingButtonComponent}
      {seeMore && mqState <= MQ_SMALL && (
        <FilterDropdownSeeMore
          filters={sortFilters(filtersRewritten)}
          onFilterChange={onFilterChange}
          tracking={tracking}
        />
      )}
    </div>
  );
};

export { PageFilters as PageFiltersForTest };

export default withMQState(PageFilters);
